// AdminNotifications — Phase 8. Compose + log notifications (fan-out to loyalty engine).
import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Send, Bell, Trash2 } from 'lucide-react';

const ROLE_OPTIONS = ['all', 'role:trainee', 'role:trainer', 'role:admin', 'role:electrician', 'role:retailer', 'role:distributor', 'role:channel_partner'];
const CHANNELS = ['inapp', 'push', 'email', 'sms', 'whatsapp'];

export default function AdminNotifications() {
    const [items, setItems] = useState([]);
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [audience, setAudience] = useState('all');
    const [cohorts, setCohorts] = useState([]);
    const [channel, setChannel] = useState('inapp');
    const [ctaLabel, setCtaLabel] = useState('');
    const [ctaUrl, setCtaUrl] = useState('');
    const [sending, setSending] = useState(false);

    const load = useCallback(async () => {
        try { const r = await api.get('/admin/notifications'); setItems(r.data || []); }
        catch { toast.error('Failed to load'); }
    }, []);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get('/admin/cohorts').then((r) => setCohorts(r.data || [])).catch(() => { /* ignore */ });
    }, []);

    const send = async () => {
        if (!title.trim() || !message.trim()) { toast.error('Title and message required'); return; }
        setSending(true);
        try {
            await api.post('/admin/notifications', {
                title, message, audience, channel,
                cta_label: ctaLabel || null, cta_url: ctaUrl || null,
            });
            toast.success(`Notification dispatched via ${channel.toUpperCase()}`);
            setTitle(''); setMessage(''); setCtaLabel(''); setCtaUrl('');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
        finally { setSending(false); }
    };

    return (
        <div className="space-y-6" data-testid="admin-notifications-page">
            <div>
                <h1 className="font-display font-extrabold text-2xl text-slate-900">Notifications</h1>
                <p className="text-sm text-slate-500">Compose in-app, push, email, SMS, or WhatsApp notifications. Real channels are wired through the Loyalty Engine.</p>
            </div>

            <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-5 space-y-3">
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" maxLength={120} className={inputCls} data-testid="notif-title" />
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Message body…" rows="3" maxLength={600} className={inputCls} data-testid="notif-message" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="block">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Audience</div>
                        <select value={audience} onChange={(e) => setAudience(e.target.value)} className={inputCls} data-testid="notif-audience">
                            <optgroup label="Roles">
                                {ROLE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                            </optgroup>
                            {cohorts.length > 0 && (
                                <optgroup label="Cohorts">
                                    {cohorts.map(c => (
                                        <option key={c.id} value={`cohort:${c.id}`}>
                                            Cohort: {c.name}{typeof c.member_count === 'number' ? ` (${c.member_count})` : ''}
                                        </option>
                                    ))}
                                </optgroup>
                            )}
                        </select>
                    </label>
                    <label className="block">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Channel</div>
                        <select value={channel} onChange={(e) => setChannel(e.target.value)} className={inputCls} data-testid="notif-channel">
                            {CHANNELS.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
                        </select>
                    </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} placeholder="CTA label (optional)" className={inputCls} data-testid="notif-cta-label" />
                    <input value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} placeholder="CTA URL" className={inputCls} data-testid="notif-cta-url" />
                </div>
                <button onClick={send} disabled={sending}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-sm disabled:opacity-50"
                    data-testid="notif-send-btn">
                    <Send className="w-4 h-4" /> {sending ? 'Dispatching…' : 'Send notification'}
                </button>
            </div>

            <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-5">
                <div className="flex items-center gap-2 font-bold text-slate-800 mb-3"><Bell className="w-4 h-4" /> Recent ({items.length})</div>
                <div className="space-y-2">
                    {items.map(n => (
                        <div key={n.id} className="ring-1 ring-slate-200 rounded-xl p-3" data-testid={`notif-row-${n.id}`}>
                            <div className="flex items-center justify-between">
                                <div className="font-bold text-sm">{n.title}</div>
                                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-600">{n.channel}</span>
                            </div>
                            <div className="text-xs text-slate-600 mt-1">{n.message}</div>
                            <div className="text-[10px] text-slate-400 mt-1.5">{n.audience} · {new Date(n.created_at).toLocaleString()}</div>
                        </div>
                    ))}
                    {items.length === 0 && <div className="text-xs text-slate-400 italic">No notifications yet.</div>}
                </div>
            </div>
        </div>
    );
}

const inputCls = 'w-full px-3 py-2 rounded-lg ring-1 ring-slate-300 focus:ring-2 focus:ring-amber-400 focus:outline-none text-sm bg-white';
